import React from "react";
import { Clock, Dumbbell, Swords, Megaphone, Flame, Globe2 } from "lucide-react";

export const ACTIVITY_ICON = { training: Dumbbell, fighting: Swords, promo: Megaphone, pyro: Flame, tour: Globe2 };
const CLAIM_COLOR = { training: "#FF007F", promo: "#39FF14", pyro: "#FF9500", tour: "#4D9FFF" };
const SPARKS = [[-18, -16], [16, -18], [-24, 4], [26, 2], [0, -22], [-10, 14], [12, 14]];

export function SlotShell({ testId, colors, delay = 0, onClick, className = "", children, extra = {} }) {
  return (
    <div data-testid={testId} onClick={onClick} {...extra}
         className={`wr-card wr-rise rounded-2xl p-[2px] transition-transform duration-300 hover:-translate-y-1 ${onClick ? "cursor-pointer" : ""} ${className}`}
         style={{ background: `linear-gradient(140deg, ${colors.join(", ")})`, animationDelay: `${delay}ms`, "--wr-glow": colors[0] }}>
      <div className="relative h-full rounded-[14px] bg-[#110726] bg-cover bg-center p-2.5 overflow-hidden"
           style={{ backgroundImage: "linear-gradient(180deg, rgba(17,7,38,0.5), rgba(17,7,38,0.82)), url('/backgroundgraphics/Wrestlerpage_Background.webp')" }}>
        {children}
      </div>
    </div>
  );
}

function ageInfo(iso) {
  if (!iso) return { text: "just now", color: "#39FF14" };
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const text = sec < 60 ? `${sec}s ago` : sec < 3600 ? `${Math.floor(sec / 60)}m ago` : sec < 86400 ? `${Math.floor(sec / 3600)}h ago` : `${Math.floor(sec / 86400)}d ago`;
  const color = sec < 600 ? "#39FF14" : sec < 3600 ? "#00F0FF" : sec < 21600 ? "#FFE600" : "#FF007F";
  return { text, color };
}

export function TimeAgo({ iso, testId }) {
  const { text, color } = ageInfo(iso);
  return (
    <div data-testid={testId} className="flex items-center gap-2 rounded-lg px-2 py-1 mb-1.5 border-2 bg-[#0A051A]/85"
         style={{ borderColor: `${color}66`, boxShadow: `0 0 10px ${color}33` }}>
      <Clock size={18} strokeWidth={2.8} className="shrink-0" style={{ color, filter: `drop-shadow(0 0 5px ${color})` }} />
      <span className="text-[9px] font-mono uppercase tracking-widest text-[#A89BCC] hidden sm:inline">Last active</span>
      <span className="ml-auto font-num text-xl leading-none tracking-wider" style={{ color, textShadow: `0 0 8px ${color}88, 1px 1px 0 #000` }}>{text}</span>
    </div>
  );
}

export function StateBadge({ state, color, iconTestId, testId }) {
  const Icon = ACTIVITY_ICON[state];
  return (
    <div data-testid={testId} className="flex items-center gap-1 pl-1 pr-1.5 py-1 rounded-lg bg-[#0A051A]/85 backdrop-blur border-2"
         style={{ borderColor: color, boxShadow: `0 0 12px ${color}77`, "--c": color }}>
      {Icon && <Icon data-testid={iconTestId} size={16} strokeWidth={2.8} className="ro-pulse shrink-0" style={{ color }} />}
      <span className={`text-[9px] font-mono font-bold uppercase tracking-wider ${Icon ? "" : "pl-0.5"}`} style={{ color }}>{state}</span>
    </div>
  );
}

const CLAIM_IMG = { training: "Training", promo: "Promo", pyro: "Pyro", tour: "Tour" };

export function ClaimButton({ type, label, testId, onClick, className = "mt-2 w-full min-h-[44px]" }) {
  const key = CLAIM_COLOR[type] ? type : "training";
  const color = CLAIM_COLOR[key];
  const Icon = ACTIVITY_ICON[key];
  const img = `url('/ui/UI_Splash_${CLAIM_IMG[key]}.webp')`;
  const mask = { WebkitMaskImage: img, maskImage: img, WebkitMaskSize: "100% 100%", maskSize: "100% 100%" };
  const Tag = onClick ? "button" : "div";
  return (
    <Tag data-testid={testId} data-claim-type={key} onClick={onClick}
         className={`ro-claim relative ${className} flex items-center justify-center gap-1.5 px-4 py-2.5 ${onClick ? "cursor-pointer active:scale-95" : "pointer-events-none"}`}
         style={{ "--c": color }}>
      <span className="absolute inset-0 bg-no-repeat" style={{ backgroundImage: img, backgroundSize: "100% 100%" }} aria-hidden="true" />
      <span className="absolute inset-0 overflow-hidden" style={mask} aria-hidden="true"><span className="ro-claim-sweep absolute inset-y-0 left-0 w-1/3" /></span>
      {SPARKS.map(([sx, sy], i) => (
        <span key={i} className="ro-spark absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-white"
              style={{ "--sx": `${sx * 3}px`, "--sy": `${sy * 1.4}px`, animationDelay: `${i * 0.22}s`, boxShadow: `0 0 6px ${color}` }} />
      ))}
      <Icon size={18} strokeWidth={3} className="ro-claim-icon relative shrink-0 text-[#0A051A]" />
      <span className="relative font-num text-sm sm:text-lg leading-none tracking-wider text-center text-[#0A051A]" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.45)" }}>{label}</span>
    </Tag>
  );
}
